/**
 * The ONLY models this app runs — a deliberate two-model registry.
 *
 * Verified against LIVE docs.x.ai (re-checked in-browser 2026-08-31, second pass):
 *   - grok-4.6: 500k context; $2 / $0.50 cached / $6 per 1M tokens under 200k prompt
 *     tokens, $4 / $1 / $12 at ≥200k ("long context"). Reasoning guide page: effort
 *     low|medium|high(default)|xhigh, "Reasoning cannot be disabled" → no 'none'.
 *   - grok-4.3: 1M context; $1.25 / $0.20 cached / $2.50 under 200k, 2× at ≥200k
 *     (model detail page /developers/models/grok-4.3). REST spec
 *     (/developers/rest-api-reference/inference/chat.md, POST /v1/responses →
 *     `reasoning.effort`): "Only supported by grok-4.3. Possible values are `none`
 *     (disables reasoning completely), `low` (this is the default if not specified),
 *     `medium` and `high`." The May-15-2026 retirement page confirms: grok-4.3 is the
 *     MIGRATION TARGET (not retired) with "4 reasoning effort levels (none, low,
 *     medium, and high)".
 *   - grok-4.5 is intentionally ABSENT: same price as 4.6 with fewer capabilities.
 */

export interface ModelSpec {
  id: string
  label: string
  /** One-line description for the picker UI. */
  blurb: string
  /** Total context window (tokens). */
  context: number
  /** Prompt-token threshold where xAI switches the request to long-context (2×) pricing. */
  longContextAt: number
  /** USD per 1M tokens — short-context rates (long context is 2× each, per docs). */
  price: { input: number; cachedInput: number; output: number }
  /** reasoning.effort values the model supports. */
  efforts: readonly ReasoningEffort[]
}

export type ReasoningEffort = 'none' | 'low' | 'medium' | 'high' | 'xhigh'

export const MODELS: Record<string, ModelSpec> = {
  'grok-4.3': {
    id: 'grok-4.3',
    label: 'Grok 4.3',
    blurb: 'Fast & economical · 1M context',
    context: 1_000_000,
    longContextAt: 200_000,
    price: { input: 1.25, cachedInput: 0.2, output: 2.5 },
    efforts: ['none', 'low', 'medium', 'high'] as const,
  },
  'grok-4.6': {
    id: 'grok-4.6',
    label: 'Grok 4.6',
    blurb: 'Strongest · configurable up to xhigh reasoning',
    context: 500_000,
    longContextAt: 200_000,
    price: { input: 2.0, cachedInput: 0.5, output: 6.0 },
    efforts: ['low', 'medium', 'high', 'xhigh'] as const,
  },
}

export type ModelId = keyof typeof MODELS

export const MODEL_IDS = Object.keys(MODELS) as ModelId[]
export const DEFAULT_MODEL: ModelId = 'grok-4.3'

/** Coerce any string (env var, request body, stored chat column) to a registry model. */
export function resolveModel(id: string | null | undefined): ModelSpec {
  return (id && MODELS[id]) || MODELS[DEFAULT_MODEL]
}

/** Highest effort the model supports (heavy-mode synthesis wants the ceiling). */
export function maxEffort(model: ModelSpec): ReasoningEffort {
  return model.efforts[model.efforts.length - 1]
}

const EFFORT_ORDER: ReasoningEffort[] = ['none', 'low', 'medium', 'high', 'xhigh']

/** Clamp a requested effort to what the model supports: the HIGHEST supported level
 *  that does not exceed the request (medium→low on grok-4.3, xhigh→high on grok-4.3),
 *  falling back to the model's lowest level. */
export function clampEffort(model: ModelSpec, effort: ReasoningEffort): ReasoningEffort {
  if (model.efforts.includes(effort)) return effort
  const want = EFFORT_ORDER.indexOf(effort)
  for (let i = want; i >= 0; i--) {
    if (model.efforts.includes(EFFORT_ORDER[i])) return EFFORT_ORDER[i]
  }
  return model.efforts[0]
}

/**
 * Where the in-turn auto-compaction should trigger. Quality-first policy: let the
 * context grow (paying long-context rates is accepted) and compact only when the
 * window itself is at risk — 75% of the model's real context, with a hard floor so
 * a misconfigured override can never push past the window.
 */
export function compactAtTokens(model: ModelSpec): number {
  // Guarded: this module is shared with the browser bundle, where `process` may not exist.
  const envRatio = typeof process !== 'undefined' ? process.env?.AI_COMPACT_RATIO : undefined
  const ratioRaw = Number(envRatio ?? 0.75)
  const ratio = Math.min(0.9, Math.max(0.3, Number.isFinite(ratioRaw) ? ratioRaw : 0.75))
  return Math.min(Math.round(model.context * ratio), model.context - 60_000)
}
